/**
 * SpecialCardToast — transient top-center HUD banner announcing that the
 * OPPONENT just played a Special Edition card (Gold Edition etc.).
 *
 * Follows the same reused-DOM-node/class-toggling pattern as
 * js/ui/directiveTrackerHUD.js (no canvas drawing, no per-frame cost) -
 * show() is only ever called from js/commands/commandProcessor.js's _play(),
 * once per opponent special-card play, never from a render loop.
 */

const EDITION_LABELS = {
  gold: 'Gold Edition'
};

const VISIBLE_MS = 2500;
const HIDE_TRANSITION_MS = 300;

export class SpecialCardToast {
  constructor(container = document.body) {
    this.container = container;
    this.toastEl = null;
    this._hideTimer = null;
    this._queued = null; // at most one buffered toast - see show()
    this._initElements();
  }

  _initElements() {
    let el = document.getElementById('special-card-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'special-card-toast';
      el.className = 'special-card-toast hidden';
      this.container.appendChild(el);
    }
    this.toastEl = el;
  }

  /**
   * @param {Object} info
   * @param {string} info.team - the OPPONENT team that played the card
   * @param {string} info.cardName
   * @param {string} info.edition - e.g. 'gold'
   */
  show({ cardName, edition }) {
    if (!this.toastEl) return;

    // A toast is already showing - buffer at most one more so a second
    // special play right after the first isn't silently dropped, but also
    // doesn't stack multiple banners on top of each other.
    if (this._hideTimer) {
      this._queued = { cardName, edition };
      return;
    }

    this._render(cardName, edition);
  }

  _render(cardName, edition) {
    const label = EDITION_LABELS[edition] || `${edition} Edition`;
    this.toastEl.innerHTML = `
      <span class="toast-icon">👑</span>
      <span class="toast-text">Gegner spielt <strong>${this._escape(cardName)}</strong> <span class="toast-edition">(${this._escape(label)})</span></span>
    `;
    this.toastEl.classList.remove('hidden');
    // Restart the CSS entrance animation even if it's already visible.
    this.toastEl.classList.remove('toast-in');
    void this.toastEl.offsetWidth; // reflow
    this.toastEl.classList.add('toast-in');

    this._hideTimer = setTimeout(() => this._hide(), VISIBLE_MS);
  }

  _hide() {
    this._hideTimer = null;
    if (!this.toastEl) return;
    this.toastEl.classList.remove('toast-in');
    setTimeout(() => {
      if (!this.toastEl) return;
      this.toastEl.classList.add('hidden');
      if (this._queued) {
        const next = this._queued;
        this._queued = null;
        this._render(next.cardName, next.edition);
      }
    }, HIDE_TRANSITION_MS);
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }
}
